/* Reya · Instagram reels — card playback + full-screen story viewer. Vanilla, no dependencies. */
(() => {
  if (customElements.get('rr-reels')) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fineHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const ensureLoaded = (v) => {
    if (!v || v.dataset.rrLoaded) return;
    v.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src));
    v.load();
    v.dataset.rrLoaded = '1';
  };

  class RrReels extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.autoplay || 'hover';
      this.cinema = this.dataset.layout === 'cinema';
      this.cards = Array.from(this.querySelectorAll('[data-rr-reel]'));
      this.cards.forEach((card, i) => this.setupCard(card, i));
      if (this.cinema) this.setupCinema();
      else if (!reduce && this.mode !== 'tap') this.setupAutoplay();
      this.dialog = this.querySelector('.rr-story');
      if (this.dialog) {
        document.body.appendChild(this.dialog); // escape transformed / clipped ancestors
        this.setupStory();
      }
      this.onBlockSelect = (e) => {
        const card = e.target.closest && e.target.closest('[data-rr-reel]');
        if (card && this.cinema && this.cards.includes(card)) return this.goTo(this.cards.indexOf(card));
        if (card && this.cards.includes(card)) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.io && this.io.disconnect();
      this.cio && this.cio.disconnect();
      this.onResize && window.removeEventListener('resize', this.onResize);
      this.cineStop && this.cineStop();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.onDocClick && document.removeEventListener('click', this.onDocClick);
      if (this.dialog) {
        this.close(true);
        this.dialog.remove();
      }
    }

    /* ---------- Cards ---------- */
    setupCard(card, i) {
      const v = card.querySelector('.rr-reel__video');
      const bar = card.querySelector('[data-rr-progress]');
      card._v = v;
      if (v) {
        v.addEventListener('timeupdate', () => {
          if (!bar || !v.duration) return;
          const len = this.cinema && this.dataset.advance === 'true' ? Math.min(v.duration, this.slideDur()) : v.duration;
          bar.style.transform = `scaleX(${Math.min(1, v.currentTime / len)})`;
        });
        v.addEventListener('playing', () => card.classList.add('is-playing'));
        v.addEventListener('pause', () => card.classList.remove('is-playing'));
      }
      const opener = card.querySelector('[data-rr-reel-open]');
      opener && opener.addEventListener('click', () => this.open(i));
      const toggle = card.querySelector('[data-rr-reel-toggle]');
      toggle &&
        toggle.addEventListener('click', () => {
          if (v && !v.paused) {
            this.stop(card);
            toggle.setAttribute('aria-pressed', 'false');
          } else {
            this.cards.forEach((c) => c !== card && this.stop(c));
            this.play(card);
            toggle.setAttribute('aria-pressed', 'true');
          }
        });
      this.setupBloom(card);
      if (!reduce && this.mode === 'hover' && !this.cinema) {
        const on = () => fineHover() && this.play(card);
        const off = () => fineHover() && this.stop(card);
        card.addEventListener('mouseenter', on);
        card.addEventListener('mouseleave', off);
        card.addEventListener('focusin', on);
        card.addEventListener('focusout', (e) => !card.contains(e.relatedTarget) && off());
      }
    }

    /* White pill → product story card ("bloom"): click/tap/keyboard toggles, hover previews on fine pointers. */
    setupBloom(card) {
      const wrap = card.querySelector('[data-rr-bloom]');
      const btn = wrap && wrap.querySelector('[data-rr-bloom-toggle]');
      const panel = wrap && wrap.querySelector('[data-rr-bloom-card]');
      if (!btn || !panel) return;
      let t;
      const set = (open) => {
        clearTimeout(t);
        if (open) this.cards.forEach((c) => c !== card && c._bloom && c._bloom(false));
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        card.classList.toggle('is-bloom', open);
      };
      card._bloom = set;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        set(panel.hidden);
      });
      wrap.addEventListener('mouseenter', () => fineHover() && set(true));
      wrap.addEventListener('mouseleave', () => {
        if (!fineHover()) return;
        t = setTimeout(() => set(false), 250);
      });
      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !panel.hidden) {
          e.stopPropagation();
          set(false);
          btn.focus();
        }
      });
      wrap.addEventListener('focusout', (e) => !wrap.contains(e.relatedTarget) && set(false));
      if (!this.onDocClick) {
        this.onDocClick = (e) => {
          this.cards.forEach((c) => c._bloom && !c.contains(e.target) && c._bloom(false));
        };
        document.addEventListener('click', this.onDocClick);
      }
    }

    play(card) {
      card.classList.add('is-active');
      const v = card._v;
      if (!v) return;
      ensureLoaded(v);
      v.muted = true;
      const p = v.play();
      p && p.catch(() => {});
    }

    stop(card) {
      card.classList.remove('is-active');
      card._v && !card._v.paused && card._v.pause();
    }

    setupAutoplay() {
      if (!('IntersectionObserver' in window)) return;
      // Desktop "in view": every visible card plays. Touch (or hover mode on touch): only the centred card plays.
      const centred = this.mode === 'hover' || !fineHover();
      if (this.mode === 'hover' && fineHover()) return;
      this.io = new IntersectionObserver(
        (entries) => {
          if (this.dialog && !this.dialog.hidden) return;
          entries.forEach((entry) => {
            const card = entry.target;
            if (entry.isIntersecting) {
              if (centred) this.cards.forEach((c) => c !== card && this.stop(c));
              this.play(card);
            } else {
              this.stop(card);
            }
          });
        },
        centred ? { rootMargin: '-30% -38% -30% -38%', threshold: 0 } : { threshold: 0.6 }
      );
      this.cards.forEach((c) => this.io.observe(c));
    }

    /* ---------- Cinema coverflow ---------- */
    setupCinema() {
      const wrap = this.querySelector('[data-rr-cine]');
      const track = wrap && wrap.querySelector('[data-rr-cine-track]');
      this.n = this.cards.length;
      if (!wrap || !track || !this.n) return;
      this.cwrap = wrap;
      this.track = track;
      this.active = 0;
      this.cMuted = true;
      this.hold = false;
      this.visible = !('IntersectionObserver' in window);
      this.glows = Array.from(wrap.querySelectorAll('[data-rr-glow]'));
      this.glowI = 0;
      this.countEl = wrap.querySelector('[data-rr-cine-count]');
      this.typeEl = wrap.querySelector('[data-rr-cine-type]');
      this.cSound = wrap.querySelector('[data-rr-cine-sound]');
      this.mqMobile = window.matchMedia('(max-width: 749px)');

      this.cards.forEach((card, i) => {
        // Remember the original tabindex so inactive slides can leave the tab order and be restored later.
        card.querySelectorAll('a, button').forEach((el) => (el.dataset.rrTab = el.getAttribute('tabindex') || ''));
        if (card._v) {
          card._v.loop = false;
          card._v.addEventListener('ended', () => i === this.active && this.onCineEnded());
        }
        card.addEventListener(
          'click',
          (e) => {
            if (this.suppressClick || i !== this.active) {
              e.preventDefault();
              e.stopPropagation();
              if (!this.suppressClick) this.goTo(i);
            }
          },
          true
        );
      });

      const prev = wrap.querySelector('[data-rr-cine-prev]');
      const next = wrap.querySelector('[data-rr-cine-next]');
      prev && prev.addEventListener('click', () => this.goTo(this.active - 1));
      next && next.addEventListener('click', () => this.goTo(this.active + 1));
      this.cSound && this.cSound.addEventListener('click', () => this.setCineMuted(!this.cMuted));

      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goTo(this.active + 1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goTo(this.active - 1);
        }
      });
      // Hover / focus holds auto-advance (the active reel keeps looping).
      wrap.addEventListener('mouseenter', () => fineHover() && (this.hold = true));
      wrap.addEventListener('mouseleave', () => (this.hold = false));
      wrap.addEventListener('focusin', () => (this.hold = true));
      wrap.addEventListener('focusout', (e) => !wrap.contains(e.relatedTarget) && (this.hold = false));

      this.setupDrag();

      if ('IntersectionObserver' in window) {
        this.cio = new IntersectionObserver(
          ([e]) => {
            this.visible = e.isIntersecting;
            this.cineSync();
          },
          { threshold: 0.25 }
        );
        this.cio.observe(wrap);
      }
      let raf;
      this.onResize = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.layoutCine(this.active));
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      if ('ResizeObserver' in window) {
        this.cro = new ResizeObserver(this.onResize);
        this.cro.observe(this.cards[0]);
      }
      window.addEventListener('load', this.onResize, { once: true });
      this.cineStop = () => {
        clearTimeout(this.typeT);
        clearTimeout(this.bloomT);
        cancelAnimationFrame(this.imgRaf);
      };

      track.classList.add('is-ready');
      this.activate(0);
    }

    pad(x) {
      return String(x).padStart(2, '0');
    }

    width() {
      return this.cards[0].offsetWidth || 300;
    }

    wrapD(i, pos) {
      let d = i - pos;
      const n = this.n;
      if (n > 2) {
        d = ((d % n) + n) % n;
        if (d > n / 2) d -= n;
      }
      return d;
    }

    layoutCine(pos) {
      const W = this.width();
      const mobile = this.mqMobile.matches;
      this.cards.forEach((card, i) => {
        const d = this.wrapD(i, pos);
        const a = Math.abs(d);
        const sg = Math.sign(d);
        let x;
        let z = 0;
        let ry = 0;
        let sc;
        let op;
        if (mobile) {
          x = sg * W * (a <= 1 ? a * 0.9 : 0.9 + (a - 1) * 0.75);
          sc = 1 - Math.min(a, 2) * 0.12;
          op = a < 1.6 ? 1 : Math.max(0, (2.2 - a) / 0.6);
        } else {
          x = sg * W * (a <= 1 ? a * 0.84 : 0.84 + (Math.min(a, 4.5) - 1) * 0.62);
          z = -Math.min(a, 3.5) * 150;
          ry = -sg * (Math.min(a, 1) * 32 + Math.max(0, Math.min(a, 3) - 1) * 3);
          sc = 1 - Math.min(a, 4) * 0.07;
          op = a <= 4 ? 1 : Math.max(0, (4.8 - a) / 0.8);
        }
        card.style.transform = `translate3d(${x.toFixed(1)}px,0,0) scale(${sc.toFixed(3)})`;
        card.style.opacity = op.toFixed(3);
        card.style.zIndex = String(100 - Math.round(a * 10));
        card.style.visibility = op <= 0.01 ? 'hidden' : '';
        card.style.setProperty('--a', Math.min(a, 1).toFixed(3));
      });
    }

    goTo(i) {
      if (!this.cinema || !this.n) return;
      i = ((i % this.n) + this.n) % this.n;
      if (i === this.active) return this.layoutCine(i);
      this.activate(i);
    }

    activate(i) {
      this.active = i;
      this.layoutCine(i);
      const card = this.cards[i];
      this.cards.forEach((c, j) => {
        const on = j === i;
        c.classList.toggle('is-current', on);
        c.setAttribute('aria-hidden', on ? 'false' : 'true');
        c.querySelectorAll('a, button').forEach((el) => {
          if (on && el.dataset.rrTab) el.setAttribute('tabindex', el.dataset.rrTab);
          else if (on) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
        if (!on && c._bloom) c._bloom(false);
        if (!on && c._v && !c._v.paused) c._v.pause();
      });
      if (card._v && card._v.dataset.rrLoaded) {
        try {
          card._v.currentTime = 0;
        } catch (e) {}
      }
      if (this.countEl) this.countEl.textContent = `${this.pad(i + 1)} / ${this.pad(this.n)}`;
      this.glow(card);
      this.type(card.dataset.captionText || '');
      clearTimeout(this.bloomT);
      if (this.dataset.autoBloom === 'true' && card._bloom && !reduce) {
        this.bloomT = setTimeout(() => this.active === i && card._bloom(true), 1600);
      }
      this.cineSync();
    }

    cineSync() {
      if (!this.cinema || !this.n) return;
      const v = this.cards[this.active]._v;
      cancelAnimationFrame(this.imgRaf);
      const storyOpen = this.dialog && this.dialog.classList.contains('is-open');
      if (!this.visible || storyOpen) {
        this.cards.forEach((c) => c._v && !c._v.paused && c._v.pause());
        return;
      }
      // Only the active reel and its immediate neighbours load video sources.
      this.cards.forEach((c, j) => c._v && Math.abs(this.wrapD(j, this.active)) <= this.playRange() && ensureLoaded(c._v));
      if (reduce) return;
      // Neighbouring reels also play (muted) so every visible reel feels alive; far ones pause.
      this.cards.forEach((c, j) => {
        if (!c._v || j === this.active) return;
        const d = Math.abs(this.wrapD(j, this.active));
        if (d <= this.playRange()) { c._v.muted = true; c._v.loop = true; c._v.play().catch(() => {}); }
        else if (!c._v.paused) c._v.pause();
      });
      if (v) {
        v.muted = this.cMuted;
        const p = v.play();
        p &&
          p.catch(() => {
            this.setCineMuted(true);
            v.play().catch(() => {});
          });
      }
      if (this.dataset.advance === 'true' && this.n > 1) {
        // Advance after the slide duration (videos also advance early if they end first).
        const dur = (v ? this.slideDur() : parseFloat(this.dataset.imageDuration) || 6) * 1000;
        let elapsed = 0;
        let last = performance.now();
        const tick = (now) => {
          if (!this.hold) elapsed += now - last;
          last = now;
          if (elapsed >= dur) return this.goTo(this.active + 1);
          this.imgRaf = requestAnimationFrame(tick);
        };
        this.imgRaf = requestAnimationFrame(tick);
      }
    }

    slideDur() {
      return parseFloat(this.dataset.slideDuration) || 7;
    }

    playRange() {
      // Every reel visible on screen plays (muted): ~3 each side on desktop, 1 on mobile.
      return window.matchMedia('(max-width: 749px)').matches ? 1 : 4;
    }

    onCineEnded() {
      if (this.dataset.advance === 'true' && !this.hold && !reduce && this.n > 1) return this.goTo(this.active + 1);
      const v = this.cards[this.active]._v;
      try {
        v.currentTime = 0;
      } catch (e) {}
      this.cineSync();
    }

    setCineMuted(m) {
      this.cMuted = m;
      const v = this.cards[this.active] && this.cards[this.active]._v;
      if (v) v.muted = m;
      if (!m && v && v.paused && this.visible) v.play().catch(() => {});
      this.cwrap.classList.toggle('is-sound', !m);
      if (this.cSound) {
        this.cSound.setAttribute('aria-pressed', m ? 'false' : 'true');
        this.cSound.setAttribute('aria-label', m ? this.dataset.labelSound : this.dataset.labelMute);
      }
    }

    glow(card) {
      const url = card.dataset.glow;
      if (!url || this.glows.length < 2) return;
      const next = this.glows[this.glowI ^ 1];
      const cur = this.glows[this.glowI];
      const swap = () => {
        next.classList.add('is-on');
        cur.classList.remove('is-on');
        this.glowI ^= 1;
      };
      if (next.getAttribute('src') === url) return swap();
      next.onload = swap;
      next.src = url;
    }

    type(text) {
      const el = this.typeEl;
      if (!el) return;
      clearTimeout(this.typeT);
      if (reduce || !text) {
        el.textContent = text;
        el.classList.remove('is-typing');
        return;
      }
      let k = 0;
      el.textContent = '';
      el.classList.add('is-typing');
      const step = () => {
        k += 1;
        el.textContent = text.slice(0, k);
        if (k < text.length) this.typeT = setTimeout(step, 26);
        else this.typeT = setTimeout(() => el.classList.remove('is-typing'), 900);
      };
      this.typeT = setTimeout(step, 380);
    }

    setupDrag() {
      const track = this.track;
      let sx = 0;
      let lastX = 0;
      let lastT = 0;
      let vx = 0;
      let down = false;
      let moved = false;
      let pid = null;
      track.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || e.target.closest('.rr-reel__bloom')) return;
        down = true;
        moved = false;
        sx = lastX = e.clientX;
        lastT = performance.now();
        vx = 0;
        pid = e.pointerId;
      });
      track.addEventListener('pointermove', (e) => {
        if (!down || e.pointerId !== pid) return;
        const dx = e.clientX - sx;
        if (!moved && Math.abs(dx) > 8) {
          moved = true;
          track.classList.add('is-dragging');
          try {
            track.setPointerCapture(pid);
          } catch (err) {}
        }
        if (!moved) return;
        const now = performance.now();
        vx = (e.clientX - lastX) / Math.max(1, now - lastT);
        lastX = e.clientX;
        lastT = now;
        this.layoutCine(this.active - dx / (this.width() * 0.86));
      });
      const end = (e) => {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
        if (!moved) return;
        const dx = e.clientX - sx;
        const projected = dx + vx * 260; // momentum
        let steps = Math.round(-projected / (this.width() * 0.86));
        if (steps === 0 && Math.abs(dx) > 40) steps = dx < 0 ? 1 : -1;
        steps = Math.max(-3, Math.min(3, steps));
        this.suppressClick = true;
        setTimeout(() => (this.suppressClick = false), 80);
        if (steps) this.goTo(this.active + steps);
        else this.layoutCine(this.active);
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
    }

    /* ---------- Story viewer ---------- */
    setupStory() {
      const d = this.dialog;
      this.q = (sel) => d.querySelector(sel);
      this.video = this.q('[data-rr-story-video]');
      this.img = this.q('[data-rr-story-img]');
      this.backdrop = this.q('[data-rr-story-backdrop]');
      this.bars = Array.from(d.querySelectorAll('[data-rr-story-bar]'));
      this.panels = Array.from(d.querySelectorAll('[data-rr-story-panel]'));
      this.count = this.q('[data-rr-story-count]');
      this.soundBtn = this.q('[data-rr-story-sound]');
      this.pauseBtn = this.q('[data-rr-story-pause]');
      this.prevBtn = this.q('[data-rr-story-prev]');
      this.nextBtn = this.q('[data-rr-story-next]');
      this.muted = this.dataset.sound !== 'true';
      this.imageMs = (parseFloat(this.dataset.imageDuration) || 6) * 1000;

      this.q('[data-rr-story-close]').addEventListener('click', () => this.close());
      this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
      this.soundBtn.addEventListener('click', () => this.setMuted(!this.muted));
      this.pauseBtn.addEventListener('click', () => this.togglePause());
      this.video.addEventListener('click', () => this.togglePause());
      this.img.addEventListener('click', () => this.togglePause());

      this.video.addEventListener('timeupdate', () => {
        const bar = this.bars[this.index];
        if (bar && this.video.duration) bar.style.transform = `scaleX(${this.video.currentTime / this.video.duration})`;
      });
      this.video.addEventListener('ended', () => this.go(this.index + 1));
      this.video.addEventListener('play', () => this.setPaused(false));
      this.video.addEventListener('pause', () => !this.video.ended && this.setPaused(true));

      // Swipe: left/right = prev/next, down = close
      const stage = this.q('[data-rr-story-stage]');
      let sx = 0;
      let sy = 0;
      stage.addEventListener('touchstart', (e) => {
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - sx;
        const dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
        else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) this.close();
      }, { passive: true });

      this.onKey = (e) => {
        if (d.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); this.close(); }
        else if (e.key === 'ArrowRight') this.go(this.index + 1);
        else if (e.key === 'ArrowLeft') this.go(this.index - 1);
        else if (e.key === 'Tab') this.trap(e);
      };
    }

    open(i) {
      if (!this.dialog) return;
      this.lastFocus = document.activeElement;
      this.cards.forEach((c) => this.stop(c));
      this.dialog.hidden = false;
      document.documentElement.classList.add('rr-lock');
      document.addEventListener('keydown', this.onKey);
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      this.setMuted(this.muted, true);
      this.go(i);
      setTimeout(() => this.q('[data-rr-story-close]').focus(), 60);
    }

    close(silent) {
      if (!this.dialog || this.dialog.hidden) return;
      this.clearTimer();
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.dialog.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.classList.remove('rr-lock');
      const hide = () => (this.dialog.hidden = true);
      if (silent || reduce) hide();
      else setTimeout(hide, 400);
      if (!silent && this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      if (!silent && this.cinema) setTimeout(() => this.cineSync(), 30);
    }

    go(i) {
      const n = this.cards.length;
      if (i >= n) return this.close();
      if (i < 0) i = 0;
      this.index = i;
      this.clearTimer();
      const card = this.cards[i];
      const full = card.dataset.full;
      const poster = card.dataset.poster;
      this.bars.forEach((b, j) => (b.style.transform = `scaleX(${j < i ? 1 : 0})`));
      this.panels.forEach((p, j) => (p.hidden = j !== i));
      this.count.textContent = `${i + 1} / ${n}`;
      this.prevBtn.disabled = i === 0;
      if (poster) this.backdrop.src = poster;
      this.dialog.classList.toggle('has-video', !!full);
      this.soundBtn.hidden = !full;
      if (full) {
        this.img.hidden = true;
        this.video.hidden = false;
        if (poster) this.video.poster = poster;
        this.video.src = full;
        this.video.muted = this.muted;
        const p = this.video.play();
        p &&
          p.catch(() => {
            // Autoplay with sound blocked — fall back to muted.
            this.setMuted(true);
            this.video.play().catch(() => this.setPaused(true));
          });
      } else {
        this.video.pause();
        this.video.hidden = true;
        this.video.removeAttribute('src');
        this.img.hidden = false;
        this.img.src = poster || this.img.src;
        this.img.alt = card.dataset.caption || '';
        this.elapsed = 0;
        this.setPaused(false);
        this.runTimer();
      }
    }

    runTimer() {
      let last = performance.now();
      const tick = (now) => {
        if (!this.paused) this.elapsed += now - last;
        last = now;
        const bar = this.bars[this.index];
        const r = Math.min(this.elapsed / this.imageMs, 1);
        bar && (bar.style.transform = `scaleX(${r})`);
        if (r >= 1) return this.go(this.index + 1);
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    clearTimer() {
      this.raf && cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    togglePause() {
      if (!this.video.hidden) {
        if (this.video.paused) this.video.play().catch(() => {});
        else this.video.pause();
      } else {
        this.setPaused(!this.paused);
      }
    }

    setPaused(p) {
      this.paused = p;
      this.dialog.classList.toggle('is-paused', p);
      this.pauseBtn.setAttribute('aria-label', p ? this.dataset.labelPlay : this.dataset.labelPause);
    }

    setMuted(m, uiOnly) {
      this.muted = m;
      if (!uiOnly) this.video.muted = m;
      this.dialog.classList.toggle('is-sound', !m);
      this.soundBtn.setAttribute('aria-pressed', m ? 'false' : 'true');
      this.soundBtn.setAttribute('aria-label', m ? this.dataset.labelSound : this.dataset.labelMute);
    }

    trap(e) {
      const f = Array.from(
        this.dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null && !el.closest('[hidden]'));
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!this.dialog.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }
  }

  customElements.define('rr-reels', RrReels);
})();
